/**
 * Writing a strategy — creating one, or editing one into a new version (LAR-1).
 *
 * **What this restores.** SDO-1 moved authoring to Lab correctly and shipped it thin: a name field
 * and one textarea. The page it replaced had a source selector, file upload, metadata autofill, a
 * whole portfolio workflow with debounced validation and per-sleeve feedback, and a create button
 * that refused until validation passed. Those are authoring features, so they belong here; the
 * allocation editor, regime settings and target-weight preview that sat beside them are deployment
 * and execution, so they do not. Git history is a UX reference, not an ownership specification.
 *
 * **Validation gates creation, and only creation.** Creating requires a validation that says yes:
 * a strategy row that has never parsed is one every downstream reader has to defend against, and
 * the old page was right to disable the button. Saving an edit is blocked only by a validation that
 * says no — an author fixing a typo in a definition they already know parses should not have to
 * click Validate first, and the route validates server-side regardless.
 *
 * **The source mode is fixed once the strategy exists.** DSL and portfolio strategies differ in
 * their identity rule (a portfolio's id is the document's UUID, because `db_sleeve_lookup` resolves
 * against it), in five stored columns, and in what "valid" means. Converting one into the other is
 * not an edit; it is a different strategy, and offering it as a dropdown would imply otherwise.
 *
 * **The definition arrives when the editor opens, not when the strategy does (#418).** `strategy`
 * carries its size and an excerpt and a null `composer_code`; the text is fetched through
 * `labStrategyApi.definition` the first time something needs it — opening the inline editor, which
 * refuses a definition too large to edit and so never fetches one, or Validate, which fetches
 * whatever it is asked to check. Saving sends a definition only when one was edited or uploaded
 * here: a name change must not carry a definition it never loaded, and must not re-upload one it
 * did.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FlaskConical, RefreshCw, Trash2 } from 'lucide-react';
import {
  labStrategyApi,
  LabStrategyError,
  type DslValidation,
  type ExportRefusal,
  type LabSourceMode,
  type LabStrategy,
  type LabStrategyVersion,
  type PortfolioValidation,
} from '../labStrategyApi';
import { DslStrategyForm } from './DslStrategyForm';
import { PortfolioStrategyForm } from './PortfolioStrategyForm';
import { StrategySourceSelector } from './StrategySourceSelector';
import { DslValidationPanel, PortfolioValidationPanel } from './ValidationPanel';
import { DependencyList } from './DependencyList';
import { VersionHistory } from './VersionHistory';

const EMPTY_DSL =
  '(defsymphony "New Strategy" {:asset-class "EQUITIES"}\n  (weight-equal [(asset "SPY" "S&P 500")]))';

/** Long enough that a paste settles, short enough that it feels like the form answering back. */
const PORTFOLIO_DEBOUNCE_MS = 500;

interface Props {
  /** The strategy being edited, or `null` while creating one. */
  strategy: LabStrategy | null;
  onCreated: (created: LabStrategy) => void;
  onSaved: () => void;
  onDeactivated: () => void;
}

export function StrategyEditor({ strategy, onCreated, onSaved, onDeactivated }: Props) {
  const creating = strategy === null;

  const [mode, setMode] = useState<LabSourceMode>('dsl');
  const [symphonyId, setSymphonyId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [definition, setDefinition] = useState('');
  // Whether `definition` is the text — true while creating, and once the stored definition has
  // been fetched or replaced. Until then `strategy.definition_chars` says how big it is.
  const [definitionLoaded, setDefinitionLoaded] = useState(false);

  const [dslValidation, setDslValidation] = useState<DslValidation | null>(null);
  const [portfolioValidation, setPortfolioValidation] = useState<PortfolioValidation | null>(null);
  const [validating, setValidating] = useState(false);

  const [versions, setVersions] = useState<LabStrategyVersion[]>([]);
  const [refusal, setRefusal] = useState<ExportRefusal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Whether the definition on screen is still the one that was loaded. The debounced portfolio
  // check is skipped while it is: a saved portfolio already carries its dependency list and its
  // staleness flag, so validating on open would spend a round trip per sleeve to redraw what the
  // row already said.
  const untouched = useRef(true);

  useEffect(() => {
    untouched.current = true;
    setDslValidation(null);
    setPortfolioValidation(null);
    setError(null);
    setRefusal(null);

    if (strategy) {
      setMode(strategy.source_mode === 'portfolio' ? 'portfolio' : 'dsl');
      setSymphonyId(strategy.symphony_id);
      setName(strategy.name ?? '');
      setDescription(strategy.description ?? '');
      // Null from `get` (#418); a write route's response carries it, and is used if handed over.
      setDefinition(strategy.composer_code ?? '');
      setDefinitionLoaded(strategy.composer_code !== null);
    } else {
      setMode('dsl');
      setSymphonyId('');
      setName('');
      setDescription('');
      setDefinition(EMPTY_DSL);
      setDefinitionLoaded(true);
      setVersions([]);
    }
  }, [strategy]);

  /** The definition text, fetching it from the server the first time it is needed. */
  const ensureDefinition = useCallback(async (): Promise<string> => {
    if (definitionLoaded || !strategy) return definition;
    const text = await labStrategyApi.definition(strategy.id);
    setDefinition(text);
    setDefinitionLoaded(true);
    return text;
  }, [definition, definitionLoaded, strategy]);

  const stored =
    strategy && !definitionLoaded
      ? { chars: strategy.definition_chars, excerpt: strategy.definition_excerpt }
      : null;
  const definitionEmpty = stored ? stored.chars === 0 : definition.trim() === '';

  const loadVersions = useCallback(async (id: number) => {
    setVersions(await labStrategyApi.versions(id));
  }, []);

  useEffect(() => {
    if (strategy) loadVersions(strategy.id);
  }, [strategy, loadVersions]);

  /**
   * Fill in what the author did not type.
   *
   * Only into an empty field — an autofill that overwrote a name somebody chose would be a data
   * loss dressed up as a convenience, and the old page was careful about this too.
   */
  const autofill = useCallback(
    (from: { name: string | null; description: string | null; symphony_id?: string | null }) => {
      if (from.name) setName((current) => current || from.name || '');
      if (from.description) setDescription((current) => current || from.description || '');
      // The id is filled in for the same reason and with the same rule. It was not, and the field
      // sat empty while the definition carried a perfectly good id — so the server fell back to a
      // slug of the name, which is how two different strategies collided on create.
      if (from.symphony_id) setSymphonyId((current) => current || from.symphony_id || '');
    },
    [],
  );

  const editDefinition = (next: string) => {
    untouched.current = false;
    setDefinition(next);
    // An upload replaces the stored definition outright; what is on screen is now the whole text.
    setDefinitionLoaded(true);
    // Cleared on edit: a green "valid" badge left over from the previous text is worse than no
    // badge, because it is a claim about code that is no longer on screen.
    setDslValidation(null);
    setPortfolioValidation(null);
  };

  // Debounced portfolio validation. Portfolios are the one mode where waiting for a button is
  // wrong: the answer depends on the author's other strategies rather than on the text alone, so
  // "which sleeves am I still missing" is a question they are asking continuously while they edit.
  useEffect(() => {
    if (mode !== 'portfolio' || untouched.current || !definition.trim()) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setValidating(true);
      try {
        const result = await labStrategyApi.validatePortfolio(definition);
        if (cancelled) return;
        setPortfolioValidation(result);
        if (result.structurally_valid) autofill(result);
      } catch (caught) {
        if (cancelled) return;
        setPortfolioValidation({
          valid: false,
          structurally_valid: false,
          name: null,
          description: null,
          portfolio_id: null,
          errors: [caught instanceof Error ? caught.message : 'Could not validate this portfolio'],
          dependencies: [],
          all_deps_resolved: false,
        });
      } finally {
        if (!cancelled) setValidating(false);
      }
    }, PORTFOLIO_DEBOUNCE_MS);

    return () => {
      // `cancelled` guards the response as well as the timer: a slow check for text that has since
      // changed would otherwise land on top of a newer answer.
      cancelled = true;
      clearTimeout(timer);
    };
  }, [definition, mode, autofill]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof LabStrategyError ? caught.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const validate = () =>
    run(async () => {
      const text = await ensureDefinition();
      if (mode === 'portfolio') {
        const result = await labStrategyApi.validatePortfolio(text);
        setPortfolioValidation(result);
        if (result.structurally_valid) autofill(result);
      } else {
        const result = await labStrategyApi.validate(text);
        setDslValidation(result);
        if (result.valid) autofill(result);
      }
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    return run(async () => {
      if (creating && mode === 'portfolio') {
        onCreated(
          await labStrategyApi.createPortfolio({
            portfolio_json: definition,
            name: name || undefined,
            description: description || undefined,
          }),
        );
      } else if (creating) {
        onCreated(
          await labStrategyApi.create({
            composer_code: definition,
            symphony_id: symphonyId.trim() || undefined,
            name: name || undefined,
            description: description || undefined,
          }),
        );
      } else if (strategy) {
        await labStrategyApi.update(strategy.id, {
          // Only a definition that changed here. The route keeps the stored one when this is
          // absent, which is what a name or description edit means.
          ...(untouched.current ? {} : { composer_code: definition }),
          name: name || undefined,
          description: description || undefined,
        });
        onSaved();
      }
    });
  };

  const revalidate = () =>
    strategy &&
    run(async () => {
      setPortfolioValidation(await labStrategyApi.revalidateDependencies(strategy.id));
      onSaved();
    });

  const deactivate = () =>
    strategy &&
    run(async () => {
      await labStrategyApi.deactivate(strategy.id);
      onDeactivated();
    });

  const toggleShortlist = (version: LabStrategyVersion) =>
    strategy &&
    run(async () => {
      setRefusal(null);
      try {
        await labStrategyApi.shortlist(strategy.id, version.id, !version.is_shortlisted);
      } catch (caught) {
        // The shortlist route refuses for the same reasons export does (no hash, not validated),
        // and refusing early is the point of it doing so — so it is shown the same way rather than
        // as a generic failure.
        if (caught instanceof LabStrategyError && caught.status === 409) {
          setRefusal({ reason: 'not_shortlistable', message: caught.message });
          return;
        }
        throw caught;
      } finally {
        await loadVersions(strategy.id);
      }
    });

  const exportVersion = (version: LabStrategyVersion) =>
    strategy &&
    run(async () => {
      setRefusal(null);
      try {
        const { blob, filename } = await labStrategyApi.exportVersion(strategy.id, version.id);
        // The browser saves it and a person carries it. Revoked immediately after the click so a
        // long research session does not accumulate object URLs for files already downloaded.
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
      } catch (caught) {
        if (caught instanceof LabStrategyError && caught.reason) {
          setRefusal({ reason: caught.reason, message: caught.message });
          return;
        }
        throw caught;
      }
    });

  const validation = mode === 'portfolio' ? portfolioValidation : dslValidation;
  // Creating needs a yes; saving is stopped only by a no. See the module header.
  const canSubmit = creating ? validation?.valid === true : validation?.valid !== false;

  return (
    <form onSubmit={submit} className="space-y-5">
      <h1 className="text-lg font-semibold text-slate-900">
        {creating ? 'New strategy' : strategy?.name || strategy?.symphony_id}
      </h1>

      <StrategySourceSelector
        value={mode}
        // Fixed once the strategy exists — converting a DSL strategy into a portfolio is not an
        // edit, and the selector would imply it were.
        disabled={!creating || busy}
        onChange={(next) => {
          setMode(next);
          setDslValidation(null);
          setPortfolioValidation(null);
          untouched.current = false;
          setDefinition(next === 'dsl' ? EMPTY_DSL : '');
        }}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="strategy-name" className="block text-sm font-medium text-slate-700">
            Name
          </label>
          <input
            id="strategy-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Filled in from the definition if you leave it blank"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {/* Offered only in DSL creation. A portfolio's id is the document's own UUID (the sleeve
            lookup resolves against it), and an existing strategy's id is what every version,
            cache entry and exported package already refers to. */}
        {creating && mode === 'dsl' && (
          <div>
            <label htmlFor="strategy-symphony-id" className="block text-sm font-medium text-slate-700">
              Strategy ID <span className="font-normal text-slate-400">optional</span>
            </label>
            <input
              id="strategy-symphony-id"
              value={symphonyId}
              onChange={(event) => setSymphonyId(event.target.value)}
              placeholder="Read from the definition when you validate"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
            />
            {/* Worth a line, because this is the value a portfolio's `root_id` must equal for the
                sleeve lookup to find this strategy at all. */}
            <p className="mt-1 text-xs text-slate-500">
              A portfolio finds this strategy by matching its sleeve <code className="font-mono">root_id</code>{' '}
              against this id.
            </p>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="strategy-description" className="block text-sm font-medium text-slate-700">
          Description
        </label>
        <textarea
          id="strategy-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
          placeholder="Filled in from the definition if you leave it blank"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      {mode === 'portfolio' ? (
        <PortfolioStrategyForm
          value={definition}
          stored={stored}
          onLoadStored={ensureDefinition}
          disabled={busy}
          validating={validating}
          onChange={editDefinition}
        />
      ) : (
        <DslStrategyForm
          value={definition}
          stored={stored}
          onLoadStored={ensureDefinition}
          disabled={busy}
          onChange={editDefinition}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={validate}
          disabled={busy || definitionEmpty}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
        >
          Validate
        </button>
        <button
          type="submit"
          disabled={busy || !canSubmit}
          title={
            creating && !canSubmit ? 'Validate the definition before creating the strategy' : undefined
          }
          className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {creating ? 'Create' : 'Save new version'}
        </button>
        {strategy && (
          <>
            <Link
              to="/backtest"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <FlaskConical className="h-4 w-4" aria-hidden="true" /> Backtest
            </Link>
            <button
              type="button"
              onClick={deactivate}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" /> Deactivate
            </button>
          </>
        )}
      </div>

      {mode === 'portfolio' && portfolioValidation && (
        <PortfolioValidationPanel result={portfolioValidation} />
      )}
      {mode === 'dsl' && dslValidation && <DslValidationPanel result={dslValidation} />}

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {/* A saved portfolio's stored sleeve list — what it resolved to when it was last checked,
          which is not the same claim as what it would resolve to now. Hidden once a fresh check
          has run, so the two cannot sit on screen disagreeing. */}
      {strategy && strategy.source_mode === 'portfolio' && !portfolioValidation && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {strategy.needs_revalidation && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                A sleeve changed — revalidate
              </span>
            )}
            <button
              type="button"
              onClick={revalidate}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" /> Revalidate sleeves
            </button>
          </div>
          {strategy.dependencies && (
            <DependencyList
              dependencies={strategy.dependencies}
              validatedAt={strategy.last_validated_at}
            />
          )}
        </div>
      )}

      {strategy && (
        <VersionHistory
          versions={versions}
          refusal={refusal}
          busy={busy}
          onToggleShortlist={toggleShortlist}
          onExport={exportVersion}
        />
      )}
    </form>
  );
}
